/**
 * @file KomendyDetail.tsx
 * @description Profile of one slash-command (GET /api/commands/:id): goal, when to
 * use / when not, the process as a stepper, and its connections (skills it fires,
 * related commands, knowledge files). The connection graph is mounted from
 * components/commands/CommandGraph. Copy is Polish.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  X,
  AlertTriangle,
  Wrench,
  GitBranch,
  BookOpen,
  Lightbulb,
} from "lucide-react";
import { api } from "../lib/api";
import type { CommandDetail } from "../lib/api";
import { cmdIcon } from "../components/commands/icons";
import { CommandGraph } from "../components/commands/CommandGraph";

const GROUP_META: Record<string, { label: string; color: string }> = {
  plan: { label: "Planowanie & egzekucja", color: "#378ADD" },
  decyzje: { label: "Decyzje strategiczne", color: "#7F77DD" },
  wiedza: { label: "Wiedza & treść", color: "#1D9E75" },
  audyt: { label: "Audyt & review", color: "#D85A30" },
  komunikacja: { label: "Komunikacja & klienci", color: "#BA7517" },
  wbudowane: { label: "Wbudowane Claude Code", color: "#888780" },
};

export function KomendyDetail() {
  const { id = "" } = useParams();
  const [data, setData] = useState<CommandDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.commands
      .getOne(id)
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const meta = data ? GROUP_META[data.group] : null;
  const color = meta?.color ?? "#378ADD";
  const Icon = cmdIcon(data?.icon);

  return (
    <div className="animate-fade-in space-y-6 max-w-3xl">
      <Link
        to="/komendy"
        className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 no-underline"
      >
        <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Komendy
      </Link>

      {error && (
        <div className="card p-4 flex items-center gap-3 border-red-500/30">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <p className="text-xs text-gray-500 font-mono truncate">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="space-y-3">
          <div className="animate-pulse h-16 bg-surface-2 rounded-xl" />
          <div className="animate-pulse h-40 bg-surface-2 rounded-xl" />
        </div>
      )}

      {data && (
        <>
          {/* Header */}
          <div className="flex items-start gap-3">
            <span
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${color}1f`, color }}
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-accent">{data.slash}</span>
                <h1 className="text-lg font-semibold text-gray-100">{data.name}</h1>
                {data.present === false && (
                  <span className="text-[10px] text-amber-400 border border-amber-500/30 rounded px-1.5 py-0.5">
                    drift — brak na dysku
                  </span>
                )}
              </div>
              <p className="text-[11px] mt-0.5" style={{ color }}>
                {meta?.label ?? data.group}
              </p>
            </div>
          </div>

          {/* Goal */}
          <div className="card p-4">
            <p className="text-sm text-gray-200 leading-relaxed">{data.goal}</p>
          </div>

          {/* Analogy — "imagine it like..." */}
          {data.analogy && (
            <div
              className="rounded-xl p-4 flex items-start gap-3"
              style={{ backgroundColor: `${color}14`, border: `1px solid ${color}3a` }}
            >
              <Lightbulb
                className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-400"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider mb-0.5 text-gray-300">
                  Wyobraź sobie
                </p>
                <p className="text-sm text-gray-200 leading-relaxed">{data.analogy}</p>
              </div>
            </div>
          )}

          {/* When / when not */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="card p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Kiedy używać
              </h2>
              <ul className="space-y-1.5">
                {data.whenUse.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-gray-300">
                    <Check
                      className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-4">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Kiedy NIE
              </h2>
              <ul className="space-y-1.5">
                {data.whenNot.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-gray-300">
                    <X
                      className="w-3.5 h-3.5 text-red-400 flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Process */}
          {data.process.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-100 mb-3">Proces</h2>
              <ol className="space-y-2">
                {data.process.map((p) => (
                  <li key={p.step} className="card p-3 flex items-start gap-3">
                    <span
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
                      style={{ backgroundColor: `${color}1f`, color }}
                    >
                      {p.step}
                    </span>
                    <div className="min-w-0">
                      <p className="text-[13px] font-medium text-gray-100">{p.label}</p>
                      <p className="text-[12px] text-gray-400 mt-0.5">{p.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {/* Connection graph */}
          <section>
            <h2 className="text-sm font-semibold text-gray-100 mb-3">Połączenia</h2>
            <CommandGraph command={data} color={color} />
          </section>

          {/* Connections — text detail */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 mb-2">
                <Wrench className="w-3.5 h-3.5" aria-hidden="true" /> Skille
              </h3>
              {data.connections.skills.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic">—</p>
              ) : (
                <ul className="space-y-1">
                  {data.connections.skills.map((s) => (
                    <li key={s.id} className="text-[12px] text-gray-400 font-mono truncate">
                      {s.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 mb-2">
                <GitBranch className="w-3.5 h-3.5" aria-hidden="true" /> Pokrewne
              </h3>
              {data.connections.related.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic">—</p>
              ) : (
                <ul className="space-y-1">
                  {data.connections.related.map((r) => (
                    <li key={r.id} className="text-[12px]">
                      <Link to={`/komendy/${r.id}`} className="text-accent no-underline font-mono">
                        /{r.id}
                      </Link>
                      <span className="text-gray-400"> — {r.relation}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="card p-4">
              <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-300 mb-2">
                <BookOpen className="w-3.5 h-3.5" aria-hidden="true" /> Wiedza
              </h3>
              {data.connections.knowledge.length === 0 ? (
                <p className="text-[11px] text-gray-500 italic">—</p>
              ) : (
                <ul className="space-y-1">
                  {data.connections.knowledge.map((k) => (
                    <li key={k.path} className="text-[12px] text-gray-400 truncate" title={k.path}>
                      {k.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
